import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { getFirebaseDb } from "@/lib/firebase-client";

export type CustomerAddress = {
  name?: string;
  line1?: string;
  line2?: string;
  city?: string;
  /** County / district / state — kept because Snipcart's cart.update
   *  rejects an address without a province field, so we have to round-trip
   *  whatever the postcode lookup returned. */
  province?: string;
  postcode?: string;
  country?: string;
  phone?: string;
};

export type CustomerProfile = {
  /** Phone number in E.164 form (e.g. "+447700900123"). The primary identity. */
  phone: string | null;
  /** Captured from Snipcart on the first completed order; not the sign-in identity. */
  email: string | null;
  displayName: string;
  /** The latest shipping address used at checkout. Overwritten on every order. */
  defaultShippingAddress: CustomerAddress | null;
  /** The latest billing address used at checkout. Overwritten on every order. */
  defaultBillingAddress: CustomerAddress | null;
};

function readAddress(value: unknown): CustomerAddress | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const source = value as Record<string, unknown>;
  return {
    name: typeof source.name === "string" ? source.name : undefined,
    line1: typeof source.line1 === "string" ? source.line1 : undefined,
    line2: typeof source.line2 === "string" ? source.line2 : undefined,
    city: typeof source.city === "string" ? source.city : undefined,
    province:
      typeof source.province === "string" ? source.province : undefined,
    postcode: typeof source.postcode === "string" ? source.postcode : undefined,
    country: typeof source.country === "string" ? source.country : undefined,
    phone: typeof source.phone === "string" ? source.phone : undefined,
  };
}

/** Reads the customer's Firestore profile, or null if it does not exist yet. */
export async function getCustomerProfile(
  uid: string,
): Promise<CustomerProfile | null> {
  const snapshot = await getDoc(doc(getFirebaseDb(), "customers", uid));
  if (!snapshot.exists()) {
    return null;
  }
  const data = snapshot.data();
  return {
    phone: typeof data.phone === "string" ? data.phone : null,
    email: typeof data.email === "string" && data.email ? data.email : null,
    displayName: typeof data.displayName === "string" ? data.displayName : "",
    defaultShippingAddress: readAddress(data.defaultShippingAddress),
    defaultBillingAddress: readAddress(data.defaultBillingAddress),
  };
}

/**
 * Creates the profile document on first sign-in if it is not already there.
 * Seeds it with the phone number only — email + addresses arrive from the
 * first completed order (or from the user editing them on the account page).
 */
export async function ensureCustomerProfile(
  uid: string,
  phone: string | null,
): Promise<void> {
  const reference = doc(getFirebaseDb(), "customers", uid);
  const snapshot = await getDoc(reference);
  if (snapshot.exists()) {
    return;
  }
  await setDoc(reference, {
    phone,
    email: null,
    displayName: "",
    defaultShippingAddress: null,
    defaultBillingAddress: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Recursively strips `undefined` values from a payload. Firestore's setDoc
 * rejects any field whose value is `undefined` (it accepts `null` or the key
 * being omitted). Callers can build a partial CustomerAddress with optional
 * fields without worrying about which ones happen to be undefined.
 */
function stripUndefined(value: unknown): unknown {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (Array.isArray(value)) {
    return value
      .map((item) => stripUndefined(item))
      .filter((item) => item !== undefined);
  }
  if (typeof value === "object") {
    const cleaned: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      const sanitised = stripUndefined(val);
      if (sanitised !== undefined) {
        cleaned[key] = sanitised;
      }
    }
    return cleaned;
  }
  return value;
}

/** Merges editable profile fields. Preserves createdAt and anything else. */
export async function saveCustomerProfile(
  uid: string,
  profile: Partial<CustomerProfile>,
): Promise<void> {
  const payload: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };
  if (profile.displayName !== undefined) {
    payload.displayName = profile.displayName;
  }
  if (profile.phone !== undefined) payload.phone = profile.phone;
  if (profile.email !== undefined) payload.email = profile.email;
  if (profile.defaultShippingAddress !== undefined) {
    payload.defaultShippingAddress =
      profile.defaultShippingAddress === null
        ? null
        : stripUndefined(profile.defaultShippingAddress);
  }
  if (profile.defaultBillingAddress !== undefined) {
    payload.defaultBillingAddress =
      profile.defaultBillingAddress === null
        ? null
        : stripUndefined(profile.defaultBillingAddress);
  }
  await setDoc(doc(getFirebaseDb(), "customers", uid), payload, { merge: true });
}
